import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { DocumentReference } from "../ars_ldm";
import { useArsContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ArsText } from "../inputs/ArsText";
import { isAnalysisProgrammingCodeTemplate } from "../typeGuards/isAnalysisProgrammingCodeTemplate";
import { DocumentReferenceControl } from "./DocumentReferenceControl";

/**
 * Props for the AnalysisProgrammingCodeTemplateControl component.
 */
export interface AnalysisProgrammingCodeTemplateControlProps {
  /** RFC 6901 JSON Pointer path to the AnalysisProgrammingCodeTemplate in ArsContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
}

/**
 * A component for managing AnalysisProgrammingCodeTemplate entities.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the AnalysisProgrammingCodeTemplate in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the programming code template editor.
 */
export const AnalysisProgrammingCodeTemplateControl = ({
  dataPath,
  disabled = false,
}: AnalysisProgrammingCodeTemplateControlProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const codeTemplate = getFromDataPath(dataPath, state, isAnalysisProgrammingCodeTemplate);

  /**
   * Handles adding a document reference for the code template.
   */
  const handleAddDocumentReference = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && codeTemplate && codeTemplate.documentRef === undefined) {
      const newDocumentReference: DocumentReference = {
        referenceDocumentId: "",
        pageRefs: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/documentRef`,
        value: newDocumentReference,
      });
    }
  };

  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3">
      <ArsText
        dataPath={`${dataPath}/context`}
        id={`${idRoute}-context`}
        label="Context"
        labelWidth={2}
        placeholder="Enter context"
        disabled={disabled}
      />

      <ArsText
        dataPath={`${dataPath}/code`}
        id={`${idRoute}-code`}
        label="Code"
        labelWidth={2}
        placeholder="Enter code"
        type="textarea"
        debounceMilliseconds={2000}
        rows={6}
        disabled={disabled}
      />

      {codeTemplate?.documentRef && (
        <DocumentReferenceControl
          dataPath={`${dataPath}/documentRef`}
          disabled={disabled}
          siblingCount={1}
        />
      )}

      <AsyncButton
        id={`${idRoute}-add-document-reference`}
        onClick={handleAddDocumentReference}
        className="d-flex align-items-center btn-sm btn-success mb-3"
        disabled={disabled || codeTemplate?.documentRef !== undefined}
      >
        <span className="me-2">Add document reference</span>
        <PlusCircle />
      </AsyncButton>
    </div>
  );
};

AnalysisProgrammingCodeTemplateControl.displayName = "AnalysisProgrammingCodeTemplateControl";
